import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';

const CharacterCostumes = ({ characterId, costumes = [], onSave, isNew = false, selectedCostume, onSelectCostume }) => {
    const { t } = useTranslation();
    const [isEditingNew, setIsEditingNew] = useState(isNew);
    const [originalId, setOriginalId] = useState(null);
    const [formData, setFormData] = useState({
        card_id: '',
        title_en: '',
        title_ja: '',
        reference: 'Original',
        stars_initial: 1,
        release_ja: '',
        release_en: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Sync form with selection
    useEffect(() => {
        if (isEditingNew) {
            setFormData({
                card_id: '',
                title_en: '',
                title_ja: '',
                reference: 'Original',
                stars_initial: 1,
                release_ja: '',
                release_en: ''
            });
            setOriginalId(null);
        } else if (selectedCostume) {
            setFormData({
                card_id: selectedCostume.card_id,
                title_en: selectedCostume.title_en || '',
                title_ja: selectedCostume.title_ja || '',
                reference: selectedCostume.reference || 'Original',
                stars_initial: selectedCostume.stars_initial || 1,
                release_ja: selectedCostume.release_ja ? new Date(selectedCostume.release_ja).toISOString().split('T')[0] : '',
                release_en: selectedCostume.release_en ? new Date(selectedCostume.release_en).toISOString().split('T')[0] : ''
            });
            setOriginalId(selectedCostume.card_id);
        }
        setSuccess(false);
        setError(null);
    }, [selectedCostume, isEditingNew]);

    const handleSelect = (costume) => {
        setIsEditingNew(false);
        onSelectCostume(costume);
    };

    const handleAddNew = () => {
        setIsEditingNew(true);
        onSelectCostume(null); // Deselect current
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (success) setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isNew) {
            onSave(formData);
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Determine if update based on presence of originalId (if we selected something) 
            // OR strict isEditingNew flag. If originalId is null, it's a create.
            const isUpdate = !isEditingNew && originalId !== null;

            // If updating, target the ORIGINAL ID (to allow renaming if backend supports it).
            // If creating, target /cards base.
            const url = isUpdate
                ? `${API_BASE_URL}/cards/${originalId}`
                : `${API_BASE_URL}/cards`;

            const method = isUpdate ? 'PUT' : 'POST';

            const payload = {
                ...formData,
                release_ja: formData.release_ja || null,
                release_en: formData.release_en || null,
                Id_Personaje: characterId
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || t('CharacterCostumes.error_save'));
            }

            const data = await response.json();

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);

            // Notify parent to refresh list
            if (onSave) onSave(data.card);

            // If created new, switch to it
            if (!isUpdate && data.card) {
                setIsEditingNew(false);
                onSelectCostume(data.card);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('CharacterCostumes.confirm_delete'))) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/cards/${formData.card_id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                throw new Error(t('CharacterCostumes.error_delete'));
            }

            // Notify parent to refresh list (pass null to indicate deletion/refresh)
            if (onSave) onSave(null);
            onSelectCostume(null); // Deselect
            setSuccess(true); // Reuse success state for message
            setTimeout(() => setSuccess(false), 3000);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{t('CharacterCostumes.title')}</h3>

                {!isNew && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                        {costumes.map(c => (
                            <div
                                key={c.card_id}
                                onClick={() => handleSelect(c)}
                                className={`cursor-pointer p-3 rounded-lg border transition-all ${selectedCostume?.card_id === c.card_id && !isEditingNew
                                    ? 'border-primary-green bg-green-50 ring-2 ring-primary-green ring-opacity-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <div className="text-xs text-gray-500 mb-1">{c.reference}</div>
                                <div className="font-bold text-gray-800 truncate" title={c.title_en}>{c.title_en}</div>
                                <div className="text-xs text-gray-400 truncate">{c.title_ja}</div>
                            </div>
                        ))}

                        {/* Add New Button */}
                        <button
                            onClick={handleAddNew}
                            className={`flex flex-col items-center justify-center p-3 rounded-lg border border-dashed transition-all ${isEditingNew
                                ? 'border-primary-green bg-green-50 ring-2 ring-primary-green ring-opacity-50'
                                : 'border-gray-300 hover:border-primary-green hover:bg-gray-50 text-gray-500 hover:text-primary-green'
                                }`}
                        >
                            <span className="text-2xl font-light mb-1">+</span>
                            <span className="text-sm font-medium">{t('CharacterCostumes.button_new')}</span>
                        </button>
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 border-t border-gray-100 pt-6">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-gray-700">
                        {isEditingNew
                            ? t('CharacterCostumes.header_creating')
                            : t('CharacterCostumes.header_editing', { title: formData.title_en || 'Sin Título' })
                        }
                    </h4>
                    {isEditingNew && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{t('CharacterCostumes.badge_new')}</span>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Card ID */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_card_id')}
                        </label>
                        <input
                            type="number"
                            name="card_id"
                            value={formData.card_id}
                            onChange={handleChange}
                            // disabled={!isEditingNew} // Commented out to allow manual entry/fix
                            className={`w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none ${!isEditingNew ? 'bg-gray-50' : ''}`}
                            required
                            placeholder="e.g. 100101"
                        />
                        {!isEditingNew && (
                            <p className="text-xs text-gray-500 mt-1">{t('CharacterCostumes.helper_id')}</p>
                        )}
                    </div>

                    {/* Reference (Original, Alt, etc) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_reference')}
                        </label>
                        <input
                            type="text"
                            name="reference"
                            value={formData.reference}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none"
                            required
                            placeholder="Original"
                        />
                    </div>

                    {/* Title EN */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_title_en')}
                        </label>
                        <input
                            type="text"
                            name="title_en"
                            value={formData.title_en}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none"
                            required
                            placeholder="e.g. Special Dreamer"
                        />
                    </div>

                    {/* Title JA */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_title_ja')}
                        </label>
                        <input
                            type="text"
                            name="title_ja"
                            value={formData.title_ja}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none"
                            required
                            placeholder="e.g. スペシャルドリーマー"
                        />
                    </div>

                    {/* Initial Stars */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_stars')}
                        </label>
                        <select
                            name="stars_initial"
                            value={formData.stars_initial}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none"
                        >
                            {[1, 2, 3, 4, 5].map(num => (
                                <option key={num} value={num}>{num} ★</option>
                            ))}
                        </select>
                    </div>

                    {/* Release Dates */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_release_jp')}
                        </label>
                        <input
                            type="date"
                            name="release_ja"
                            value={formData.release_ja}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('CharacterCostumes.label_release_en')}
                        </label>
                        <input
                            type="date"
                            name="release_en"
                            value={formData.release_en}
                            onChange={handleChange}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green outline-none"
                        />
                    </div>
                </div>

                {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                        {t('CharacterCostumes.success_msg')}
                    </div>
                )}

                <div className="flex justify-between pt-4">
                    {!isEditingNew && !isNew ? (
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={loading}
                            className="px-4 py-2 bg-red-100 text-red-600 rounded-lg font-medium hover:bg-red-200 transition-colors"
                        >
                            {t('CharacterCostumes.button_delete')}
                        </button>
                    ) : <div></div>}

                    <button
                        type="submit"
                        disabled={loading}
                        className={`px-4 py-2 bg-primary-green text-white rounded-lg font-medium hover:bg-green-600 transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {loading
                            ? t('CharacterCostumes.button_saving')
                            : (isNew
                                ? t('CharacterCostumes.button_confirm_new')
                                : (isEditingNew
                                    ? t('CharacterCostumes.button_create')
                                    : t('CharacterCostumes.button_save')
                                )
                            )
                        }
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CharacterCostumes;
// Revisado: 2025-12-16
